import * as React from "react";
import { ArrowDown } from "lucide-react";

import { ChatGroupContext } from "./ChatMessage";
import { formatRelativeDay, isSameDay } from "./lib/date";
import { headingTag, useHeadingLevel, type HeadingLevel, type HeadingTag } from "./lib/heading";
import { cn } from "./lib/utils";
import { Button } from "./ui/button";
import { ScrollArea } from "./ui/scroll-area";
import { Skeleton } from "./ui/skeleton";

/**
 * v2 ChatThread (redesign phase 5, #455; break #138): the scrolling message
 * list of a chat, replacing AssistantMessageFeed's frame and the three
 * scroll-to-bottom copies in Chat, ServiceAssistant and InlineAssistant.
 * Presentational: the app maps its messages to items (04, "Chat").
 *
 * - Scroll: ScrollArea with a named, focusable viewport. Sticks to the bottom
 *   while the reader is within 80px of it (new items, streamed text, steps
 *   that grow), never pulls a reader back down, except for the reader's own
 *   message: a new user item at the end always scrolls to the bottom.
 *   `autoScroll={false}` pauses both (the add-in's fact-fly).
 * - "Zu neuen Nachrichten" shows when the reader is more than half a pane
 *   above the bottom; it scrolls down and moves focus to the last message.
 * - Day separators: "Heute", "Gestern", else "6. Okt. 2026" (`formatRelativeDay`), as headings;
 *   `daySeparators={false}` leaves them out (a new day then starts a new group).
 * - Grouping: consecutive items of one author within 5 minutes on the same
 *   day are `continued` (context for the message parts) and sit 4px apart.
 * - Announcements: the list is NOT a `role="log"` (an implicit live region
 *   would read every streamed chunk and repeat nested step and error
 *   announcements). One polite status says `announce ?? labels.newAnswer`
 *   once per assistant item when it is `done`; items present at mount and
 *   errors (their Alert announces itself) are never announced.
 */

export interface ChatThreadItem {
  id: string;
  author: "user" | "assistant";
  time: Date;
  /** Assistant items: `pending` while waiting / streaming, `error` when the run failed. Default `done`. */
  state?: "pending" | "done" | "error";
  /** What the status says when this answer is done; default `labels.newAnswer`. */
  announce?: string;
  /** A ChatUserMessage or ChatAssistantMessage. */
  content: React.ReactNode;
}

export interface ChatThreadLabels {
  /** The scroll pane's name ("Nachrichtenverlauf"). */
  region: string;
  /** Day separator for today ("Heute"). */
  today: string;
  /** Day separator for yesterday ("Gestern"). */
  yesterday: string;
  /** "Zu neuen Nachrichten". */
  jumpToLatest: string;
  /** "Neue Antwort von lytra". */
  newAnswer: string;
  /** Status while older messages load ("Ältere Nachrichten werden geladen"). */
  loadingOlder: string;
}

export interface ChatThreadProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "children"> {
  items: readonly ChatThreadItem[];
  size?: "md" | "sm";
  /** Shown centred when there are no items (an EmptyState with suggestions). */
  empty?: React.ReactNode;
  /** Message-shaped skeletons at the top. */
  loadingOlder?: boolean;
  /** Follow new content while near the bottom. Default true. */
  autoScroll?: boolean;
  viewportRef?: React.Ref<HTMLDivElement>;
  /** Extra classes for the content column inside the scroll pane (e.g. a max width). */
  contentClassName?: string;
  /** Show the day separators. Default true. */
  daySeparators?: boolean;
  /** Level of the day separators. Default: the context level (h3 in a pane, h3 on its own). */
  dayHeadingLevel?: HeadingLevel;
  /** "Today" for the separators; defaults to the current date. */
  now?: Date;
  labels: ChatThreadLabels;
}

const NEAR_BOTTOM_PX = 80;
/**
 * Content that changes size this soon after the reader clicked or pressed a key in the
 * thread is their own doing (a source expanded or collapsed), not new output: following
 * it to the bottom would move the row they just clicked away from under the pointer.
 */
const OWN_RESIZE_MS = 1000;
const GROUP_GAP_MS = 5 * 60 * 1000;

function DaySeparator({ label, as: Heading }: { label: string; as: HeadingTag }) {
  return (
    <Heading className="my-4 flex items-center gap-3 font-sans text-caption text-ink-muted first:mt-0">
      <span aria-hidden="true" className="h-px flex-1 bg-divider" />
      {label}
      <span aria-hidden="true" className="h-px flex-1 bg-divider" />
    </Heading>
  );
}

function OlderSkeleton({ label }: { label: string }) {
  return (
    <div role="status" className="mb-4 flex flex-col gap-4">
      <span className="sr-only">{label}</span>
      <Skeleton aria-hidden="true" className="h-10 w-1/2 self-end rounded-xl" />
      <div aria-hidden="true" className="flex gap-2">
        <Skeleton shape="circle" className="size-6 shrink-0" />
        <div className="flex flex-1 flex-col">
          <Skeleton shape="line" className="w-1/3" />
          <Skeleton shape="line" />
          <Skeleton shape="line" className="w-2/3" />
        </div>
      </div>
    </div>
  );
}

export const ChatThread = React.forwardRef<HTMLDivElement, ChatThreadProps>(function ChatThread(
  {
    items,
    size = "md",
    empty,
    loadingOlder = false,
    autoScroll = true,
    viewportRef,
    contentClassName,
    daySeparators = true,
    dayHeadingLevel,
    now,
    labels,
    className,
    ...props
  },
  ref,
) {
  const dayHeading = headingTag(useHeadingLevel(dayHeadingLevel, 3));
  const viewport = React.useRef<HTMLDivElement | null>(null);
  const content = React.useRef<HTMLDivElement>(null);
  const lastItem = React.useRef<HTMLDivElement>(null);
  const nearBottom = React.useRef(true);
  const [showJump, setShowJump] = React.useState(false);

  const setViewport = React.useCallback(
    (el: HTMLDivElement | null) => {
      viewport.current = el;
      if (typeof viewportRef === "function") viewportRef(el);
      else if (viewportRef) (viewportRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
    },
    [viewportRef],
  );

  const measure = React.useCallback(() => {
    const el = viewport.current;
    if (!el) return;
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    nearBottom.current = distance < NEAR_BOTTOM_PX;
    setShowJump(distance > el.clientHeight / 2);
  }, []);

  // Follow growth (new items, streamed text, steps) while the reader is at the bottom.
  const autoScrollRef = React.useRef(autoScroll);
  autoScrollRef.current = autoScroll;
  const follow = React.useCallback(() => {
    const el = viewport.current;
    if (!el) return;
    if (autoScrollRef.current && nearBottom.current) el.scrollTop = el.scrollHeight;
    measure();
  }, [measure]);

  // A message the reader just sent is appended as a user item: jump to the bottom even
  // when scrolled up, so they see it and the answer that follows. Only a new LAST item
  // counts (not older ones prepended, not the items at mount); autoScroll={false} still wins.
  const lastId = React.useRef<string | null>(null);
  React.useLayoutEffect(() => {
    const last = items.length > 0 ? items[items.length - 1] : undefined;
    // Appended: the previous last item is still there, now before the new one.
    const appended = !!last && last.id !== lastId.current && items.slice(0, -1).some((i) => i.id === lastId.current);
    if (appended && last.author === "user") nearBottom.current = true;
    lastId.current = last?.id ?? null;
    follow();
  }, [items, follow]);

  // Growth the reader caused (OWN_RESIZE_MS) is measured but not followed. New and
  // streamed items still are: they come through the items effect above.
  const interactedAt = React.useRef(Number.NEGATIVE_INFINITY);
  React.useEffect(() => {
    const el = viewport.current;
    if (!el) return;
    const interacted = () => {
      interactedAt.current = performance.now();
    };
    const resized = () => {
      if (performance.now() - interactedAt.current < OWN_RESIZE_MS) measure();
      else follow();
    };
    el.addEventListener("scroll", measure, { passive: true });
    el.addEventListener("pointerdown", interacted, { capture: true, passive: true });
    el.addEventListener("keydown", interacted, { capture: true, passive: true });
    let observer: ResizeObserver | undefined;
    if (typeof ResizeObserver !== "undefined" && content.current) {
      observer = new ResizeObserver(resized);
      observer.observe(content.current);
    }
    return () => {
      el.removeEventListener("scroll", measure);
      el.removeEventListener("pointerdown", interacted, { capture: true });
      el.removeEventListener("keydown", interacted, { capture: true });
      observer?.disconnect();
    };
  }, [measure, follow]);

  const jumpToLatest = () => {
    const el = viewport.current;
    if (!el) return;
    const reduce = typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (typeof el.scrollTo === "function") el.scrollTo({ top: el.scrollHeight, behavior: reduce ? "auto" : "smooth" });
    else el.scrollTop = el.scrollHeight;
    nearBottom.current = true;
    setShowJump(false);
    // The button unmounts: focus goes to the newest message, so reading continues there.
    const wrapper = lastItem.current;
    const target = wrapper?.querySelector<HTMLElement>("article") ?? wrapper;
    if (target) {
      if (!target.hasAttribute("tabindex")) target.tabIndex = -1;
      target.focus({ preventScroll: true });
    }
  };

  // One announcement per finished assistant answer; silent for what was there at mount.
  const announced = React.useRef<Set<string> | null>(null);
  const [announcement, setAnnouncement] = React.useState("");
  const flip = React.useRef(false);
  const doneSignature = items
    .filter((i) => i.author === "assistant" && (i.state ?? "done") === "done")
    .map((i) => i.id)
    .join("\u0001");
  React.useEffect(() => {
    const done = items.filter((i) => i.author === "assistant" && (i.state ?? "done") === "done");
    if (announced.current === null) {
      announced.current = new Set(done.map((i) => i.id));
      return;
    }
    const fresh = done.filter((i) => !announced.current!.has(i.id));
    if (fresh.length === 0) return;
    for (const i of fresh) announced.current.add(i.id);
    // A trailing no-break space alternates so the same words twice in a row still change the text.
    flip.current = !flip.current;
    setAnnouncement(fresh.map((i) => i.announce ?? labels.newAnswer).join(". ") + (flip.current ? " " : ""));
    // `doneSignature` captures every change that matters; items and labels are read from the same render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doneSignature]);

  const today = now ?? new Date();

  return (
    <div ref={ref} data-slot="chat-thread" className={cn("relative flex min-h-0 flex-col", className)} {...props}>
      <ScrollArea className="min-h-0 flex-1" viewportRef={setViewport} viewportLabel={labels.region}>
        <div ref={content} className={cn("flex flex-1 flex-col", size === "md" ? "p-4" : "px-2 py-3", contentClassName)}>
          {loadingOlder && <OlderSkeleton label={labels.loadingOlder} />}
          {items.length === 0 && !loadingOlder ? (
            <div className="flex flex-1 items-center justify-center">{empty}</div>
          ) : (
            items.map((item, index) => {
              const prev = items[index - 1];
              const newDay = !prev || !isSameDay(prev.time, item.time);
              const continued =
                !newDay && prev.author === item.author && item.time.getTime() - prev.time.getTime() <= GROUP_GAP_MS;
              const last = index === items.length - 1;
              const separated = newDay && daySeparators;
              return (
                <React.Fragment key={item.id}>
                  {separated && (
                    <DaySeparator
                      as={dayHeading}
                      label={formatRelativeDay(item.time, { now: today, style: "short-month", labels })}
                    />
                  )}
                  <div
                    ref={last ? lastItem : undefined}
                    data-slot="chat-thread-item"
                    data-continued={continued ? "" : undefined}
                    className={cn(index > 0 && !separated && (continued ? "mt-1" : "mt-4"))}
                  >
                    <ChatGroupContext.Provider value={{ continued }}>{item.content}</ChatGroupContext.Provider>
                  </div>
                </React.Fragment>
              );
            })
          )}
        </div>
      </ScrollArea>

      {showJump && (
        <div className="pointer-events-none absolute inset-x-0 bottom-3 flex justify-center">
          {/* A solid surface under the outline button: it floats over text. */}
          <span className="pointer-events-auto rounded-md bg-card shadow-popover">
            <Button variant="outline" size="sm" leadingIcon={ArrowDown} onClick={jumpToLatest}>
              {labels.jumpToLatest}
            </Button>
          </span>
        </div>
      )}

      <span role="status" className="sr-only" data-slot="chat-thread-status">
        {announcement}
      </span>
    </div>
  );
});
